'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  crosswordMatches,
  loadWords,
  rankWordle,
  spellingBee,
  wordleCandidates,
} from '@/lib/browser-word-solvers';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

type Variant = 'wordle' | 'spelling-bee' | 'crossword';

const box: React.CSSProperties = {
  width: '2.6rem',
  textAlign: 'center',
  textTransform: 'uppercase',
  fontWeight: 700,
};

export default function BrowserWordSolver({
  config,
}: {
  config?: { toolSlug?: string; heading?: string; variant?: string };
}) {
  const variant = (config?.variant ?? 'wordle') as Variant;
  const [words, setWords] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [green, setGreen] = useState(['', '', '', '', '']);
  const [yellow, setYellow] = useState(['', '', '', '', '']);
  const [grey, setGrey] = useState('');
  const [letters, setLetters] = useState('ACLRTNE');
  const [center, setCenter] = useState('E');
  const [pattern, setPattern] = useState('C?T');
  const [include, setInclude] = useState('');
  const [exclude, setExclude] = useState('');
  const reported = useRef(false);
  const touch = () => {
    if (reported.current) return;
    reported.current = true;
    void consumeToolUsage(config?.toolSlug ?? `${variant}-solver`, '').catch(() => undefined);
  };

  useEffect(() => {
    loadWords().then(setWords, (e: Error) => setError(e.message));
  }, []);

  const result = useMemo(() => {
    if (!words) return null;
    if (variant === 'wordle') {
      // Before any clue the dictionary ranking surfaces obscure words; show the common
      // opening guesses players actually use instead.
      if (!green.join('') && !yellow.join('') && !grey) return { count: 0, list: [], start: true };
      const c = rankWordle(wordleCandidates(words, { green, yellow, grey }));
      return { count: c.length, list: c.slice(0, 120) };
    }
    if (variant === 'spelling-bee') {
      const r = spellingBee(words, letters, center);
      return { count: r.length, bee: r, score: r.reduce((s, b) => s + b.points, 0) };
    }
    const r = crosswordMatches(words, pattern, include, exclude);
    return { count: r.length, list: r.slice(0, 300) };
  }, [words, variant, green, yellow, grey, letters, center, pattern, include, exclude]);

  const slotRow = (values: string[], set: (v: string[]) => void, label: string, max: number) => (
    <label className="tool-live-field tool-live-field-full">
      <span>{label}</span>
      <div style={{ display: 'flex', gap: '0.35rem' }}>
        {values.map((v, i) => (
          <input
            aria-label={`${label}, letter ${i + 1}`}
            key={i}
            maxLength={max}
            onChange={(e) => {
              touch();
              set(values.map((x, j) => (j === i ? e.target.value.replace(/[^a-z]/gi, '') : x)));
            }}
            style={{ ...box, width: max > 1 ? '3.6rem' : '2.6rem' }}
            type="text"
            value={v}
          />
        ))}
      </div>
    </label>
  );

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Word games</span>
        <h2 className="tool-live-title">{config?.heading ?? 'Word solver'}</h2>
        <p className="tool-live-subtitle">
          Uses the public-domain ENABLE dictionary (168,000 words), loaded once in your browser.
        </p>
      </div>
      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          {variant === 'wordle' ? (
            <>
              {slotRow(green, setGreen, 'Green letters (right letter, right spot)', 1)}
              {slotRow(yellow, setYellow, 'Yellow letters (in the word, not in this spot)', 5)}
              <label className="tool-live-field tool-live-field-full">
                <span>Grey letters (not in the word)</span>
                <input
                  onChange={(e) => {
                    touch();
                    setGrey(e.target.value.replace(/[^a-z]/gi, ''));
                  }}
                  style={{ textTransform: 'uppercase', letterSpacing: '0.15em' }}
                  type="text"
                  value={grey}
                />
              </label>
            </>
          ) : null}
          {variant === 'spelling-bee' ? (
            <>
              <label className="tool-live-field tool-live-field-full">
                <span>The seven letters</span>
                <input
                  maxLength={7}
                  onChange={(e) => {
                    touch();
                    setLetters(e.target.value.replace(/[^a-z]/gi, ''));
                  }}
                  style={{ textTransform: 'uppercase', letterSpacing: '0.3em', fontWeight: 700 }}
                  type="text"
                  value={letters}
                />
              </label>
              <label className="tool-live-field">
                <span>Center letter (required)</span>
                <input
                  maxLength={1}
                  onChange={(e) => {
                    touch();
                    setCenter(e.target.value.replace(/[^a-z]/gi, ''));
                  }}
                  style={box}
                  type="text"
                  value={center}
                />
              </label>
            </>
          ) : null}
          {variant === 'crossword' ? (
            <>
              <label className="tool-live-field tool-live-field-full">
                <span>Pattern: known letters, ? for blanks (e.g. C?T or ?R?NG?)</span>
                <input
                  onChange={(e) => {
                    touch();
                    setPattern(e.target.value);
                  }}
                  spellCheck={false}
                  style={{ textTransform: 'uppercase', letterSpacing: '0.2em', fontWeight: 700 }}
                  type="text"
                  value={pattern}
                />
              </label>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <label className="tool-live-field">
                  <span>Must contain</span>
                  <input onChange={(e) => setInclude(e.target.value)} type="text" value={include} />
                </label>
                <label className="tool-live-field">
                  <span>Exclude letters</span>
                  <input onChange={(e) => setExclude(e.target.value)} type="text" value={exclude} />
                </label>
              </div>
            </>
          ) : null}
          <p className="tool-live-panel-note">
            {variant === 'wordle'
              ? 'Best guesses first, ranked by how common each letter is in its spot among the remaining words. The list includes rarer dictionary words; the daily answer is usually a common one.'
              : variant === 'spelling-bee'
                ? 'Scoring as in the NYT game: 4-letter words 1 point, longer words 1 per letter, pangrams +7. The official puzzle leaves out some obscure words, so expect a few extras here.'
                : 'This matches letter patterns; it does not look up clue meanings. Add letters you know to narrow it down.'}
          </p>
        </section>
        <section className="tool-live-preview">
          {error ? <p className="status-message status-error">{error}</p> : null}
          {!result ? (
            <p className="tool-live-panel-note">Loading the dictionary…</p>
          ) : 'start' in result ? (
            <p className="tool-live-panel-note">
              Enter the colours from your guesses to see every word that still fits. Popular first
              guesses cover common letters: <strong>CRANE, SLATE, TRACE, CRATE, AUDIO</strong>.
            </p>
          ) : (
            <>
              <p className="tool-live-title" style={{ margin: 0 }}>
                {result.count.toLocaleString('en-US')} {result.count === 1 ? 'word' : 'words'}
                {'score' in result && result.score ? `, ${result.score} points` : ''}
              </p>
              {'bee' in result && result.bee ? (
                <p style={{ lineHeight: 1.9, fontVariantNumeric: 'tabular-nums' }}>
                  {result.bee.map((b) => (
                    <span
                      key={b.word}
                      style={{ marginRight: '0.6rem', fontWeight: b.pangram ? 700 : 400 }}
                    >
                      {b.word}
                      {b.pangram ? ' ★' : ''}{' '}
                    </span>
                  ))}
                </p>
              ) : (
                <p
                  style={{
                    lineHeight: 1.9,
                    textTransform: variant === 'wordle' ? 'uppercase' : 'none',
                    letterSpacing: variant === 'wordle' ? '0.05em' : 0,
                  }}
                >
                  {result.list?.map((w) => (
                    <span key={w} style={{ marginRight: '0.7rem' }}>
                      {w}{' '}
                    </span>
                  ))}
                  {result.list && result.count > result.list.length ? (
                    <em>… and {result.count - result.list.length} more</em>
                  ) : null}
                </p>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
